// ── CSV export ───────────────────────────────────────────────────────────────
// Every "Export" button in the panel used to show a success toast and produce
// no file. The rows are already loaded in the browser, so the export is done
// here rather than adding an endpoint that would re-query the same data.

type Cell = string | number | boolean | null | undefined;

function escapeCell(value: Cell): string {
  if (value === null || value === undefined) return "";
  const text = String(value);
  // Quote anything containing a delimiter, a quote or a newline; double the quotes.
  return /[",\n\r]/.test(text) ? `"${text.replace(/"/g, '""')}"` : text;
}

export function toCsv<T>(rows: T[], columns: Array<{ header: string; value: (row: T) => Cell }>): string {
  const head = columns.map((c) => escapeCell(c.header)).join(",");
  const body = rows.map((row) => columns.map((c) => escapeCell(c.value(row))).join(","));
  return [head, ...body].join("\r\n");
}

/** Triggers a download of `rows` as `<filename>-YYYY-MM-DD.csv`. */
export function downloadCsv<T>(
  filename: string,
  rows: T[],
  columns: Array<{ header: string; value: (row: T) => Cell }>
): number {
  // A BOM so Excel opens Arabic names and the SAR sign as UTF-8.
  const blob = new Blob(["﻿", toCsv(rows, columns)], { type: "text/csv;charset=utf-8" });
  const url = URL.createObjectURL(blob);
  const stamp = new Date().toISOString().slice(0, 10);

  const link = document.createElement("a");
  link.href = url;
  link.download = `${filename}-${stamp}.csv`;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);

  return rows.length;
}
